/**
 * Where each vertical's operator portal lives.
 *
 * Tier 1 holds the mapping because only Tier 1 knows there are several
 * portals; the vertical codes are opaque keys it never interprets beyond
 * lookup. Overridable per environment so staging points at staging.
 */
const OPERATOR_PORTAL_ENV_KEYS: Record<string, string> = {
  school: 'VITE_SCHOOL_PORTAL_URL',
  rental: 'VITE_PROPERTY_PORTAL_URL',
}

// Last-resort defaults. The deployed builds get these from Terraform via
// VITE_*_PORTAL_URL (see the `school_portal_url` / `property_portal_url`
// outputs), so a default is only reached locally or if that wiring breaks.
//
// The hostnames follow the convention the rest of the estate already uses --
// `<env>.<portal>.lipaflow.co.ke`, matching admin, parent and tenant, the
// .firebaserc target keys and the backend's portal settings. An earlier
// draft of this file used `schools.` and `manage.` from the architecture
// plan's diagram; neither was ever given DNS, so the redirect below sent
// mis-landed operators to a host that does not resolve.
const OPERATOR_PORTAL_DEFAULTS: Record<string, { local: string; production: string }> = {
  school: { local: 'http://localhost:3000', production: 'https://school.lipaflow.co.ke' },
  rental: { local: 'http://localhost:3004', production: 'https://property.lipaflow.co.ke' },
}

/** Absolute origin for a vertical's operator portal, or null if unknown. */
export function getOperatorPortalUrl(verticalCode: string): string | null {
  const metaEnv = import.meta.env ?? {}
  const override = metaEnv[OPERATOR_PORTAL_ENV_KEYS[verticalCode]]
  if (override) return String(override).replace(/\/$/, '')

  const defaults = OPERATOR_PORTAL_DEFAULTS[verticalCode]
  if (!defaults) return null

  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.endsWith('.local'))

  return isLocalhost ? defaults.local : defaults.production
}
